/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useRef, useState, useCallback, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { Modal, Button } from 'react-bootstrap';
import Cropper from 'react-easy-crop';
import { useAppDispatch } from '../../hooks';
import { userActions, notificationActions } from '../../redux/actions';
import { pathConstants } from '../../constants';
import { studentService } from '../../services';
import PendingChallengesSection from './PendingChallengesSection';
import NotificationBell from './NotificationBell';
import ChangePasswordModal from './ChangePasswordModal';

const HeaderContainer = styled.div`
  background: white;
  border-radius: 12px;
  padding: 20px 24px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.07);
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 24px;
  margin-bottom: 0;
  width: 100%;
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: 12px;
    gap: 12px;
    flex-wrap: wrap;
    width: 100%;
    min-width: 0;
    max-width: 100vw;
  }
`;

const AvatarSection = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex-shrink: 0;

  @media (max-width: 768px) {
    flex: 1 0 auto;
    order: 1;
  }
`;

const Avatar = styled.div<{src?: string}>`
  width: 100px;
  height: 100px;
  border-radius: 50%;
  background: ${(p) => p.src ? `url(${p.src})` : '#667eea'};
  background-size: cover;
  background-position: center;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 40px;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
  border: 3px solid white;
  cursor: pointer;
  transition: all 0.3s ease;
  flex-shrink: 0;

  &:hover {
    transform: scale(1.05);

    .edit-overlay {
      opacity: 1;
    }
  }

  @media (max-width: 768px) {
    width: 80px;
    height: 80px;
    font-size: 32px;
  }
`;

const EditOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.3s ease;
  cursor: pointer;
`;

const StudentInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;

  @media (max-width: 768px) {
    flex: 1;
    min-width: 150px;
  }
`;

const StudentNameRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  flex-wrap: wrap;
`;

const StudentName = styled.h2`
  margin: 0;
  font-size: 20px;
  color: #0f172a;
  font-weight: 700;
  line-height: 1.2;

  @media (max-width: 768px) {
    font-size: 18px;
  }
`;

const StudentUserNameRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
`;

const StudentUserName = styled.p`
  margin: 0;
  color: #667eea;
  font-weight: 600;
  font-size: 13px;
`;

const PointsSection = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
`;

const Points = styled.span`
  font-size: 14px;
  font-weight: 700;
  color: #0f172a;

  @media (max-width: 768px) {
    font-size: 13px;
  }
`;

const Star = styled.span`
  font-size: 16px;

  @media (max-width: 768px) {
    font-size: 14px;
  }
`;

const LogoutButton = styled.button`
  padding: 10px 20px;
  background: #ff6b6b;
  color: white;
  border: none;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  flex-shrink: 0;

  &:hover {
    background: #ff5252;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(255, 107, 107, 0.3);
  }

  @media (max-width: 768px) {
    padding: 8px 16px;
    font-size: 14px;
    order: 2;
  }
`;

const HeaderActions = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    order: 2;
  }
`;

const VocabCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.07);
  margin-bottom: 0;
  width: 100%;
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: 12px;
    width: 100%;
    min-width: 0;
    max-width: 100vw;
  }
`;

const VocabHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  gap: 12px;

  @media (max-width: 768px) {
    flex-wrap: nowrap;
  }
`;

const VocabTitle = styled.h3`
  margin: 0;
  font-size: 20px;
  color: #0f172a;
  font-weight: 700;

  @media (max-width: 768px) {
    font-size: 18px;
  }
`;

const VocabButton = styled.button`
  background: linear-gradient(90deg, #007bff 0%, #00bfff 100%);
  color: white;
  border: none;
  border-radius: 8px;
  padding: 10px 18px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(0, 123, 255, 0.3);
  white-space: nowrap;
  width: fit-content;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(0, 123, 255, 0.4);
  }

  &:active {
    transform: translateY(0);
  }

  @media (max-width: 768px) {
    width: fit-content;
    padding: 9px 14px;
    font-size: 12px;
  }
`;

const StatsGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const StatItem = styled.div<{ bg: string; color: string }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 12px;
  border-radius: 10px;
  font-size: 12px;
  font-weight: 600;
  background: ${({ bg }) => bg};
  color: ${({ color }) => color};

  .label { opacity: 0.85; }
  .value { font-size: 14px; font-weight: 700; }
`;

const HiddenFileInput = styled.input`
  display: none;
`;

interface StudentProfileSectionProps {
  currentUser: any;
  onLogout: () => void;
  onReviewClick?: () => void;
}

const StudentProfileSection: React.FC<StudentProfileSectionProps> = ({ currentUser, onLogout, onReviewClick }) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const [showAvatarModal, setShowAvatarModal] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [croppedImage, setCroppedImage] = useState<string | null>(null);
  const [showCropper, setShowCropper] = useState(false);
  const [isSavingAvatar, setIsSavingAvatar] = useState(false);

  const [vocabStats, setVocabStats] = useState<any>(null);
  const [studentInfo, setStudentInfo] = useState<any>(null);

  useEffect(() => {
    const fetchVocabStats = async () => {
      try {
        const stats = await studentService.getFlashcardStats();
        setVocabStats(stats);
      } catch (error) {
        console.error('Error fetching vocab stats:', error);
      }
    };

    const fetchStudentInfo = async () => {
      try {
        if (currentUser?.studentId) {
          const student = await studentService.getById(currentUser.studentId);
          setStudentInfo(student);
        }
      } catch (error) {
        console.error('Error fetching student info:', error);
      }
    };

    fetchVocabStats();
    fetchStudentInfo();
  }, [currentUser?.studentId]);

  const handleAvatarClick = () => {
    fileInputRef.current?.click();
  };

  const handleVocabClick = () => {
    navigate(pathConstants.STUDENT_FLASHCARD);
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      dispatch(notificationActions.addNotification('Kích thước file tối đa 10MB', 'DANGER'));
      return;
    }

    if (!file.type.startsWith('image/')) {
      dispatch(notificationActions.addNotification('Vui lòng chọn một file hình ảnh', 'DANGER'));
      return;
    }

    const reader = new FileReader();
    reader.addEventListener('load', () => {
      setImageSrc(reader.result as string);
      setShowCropper(true);
      setCroppedImage(null);
      setShowAvatarModal(true);
    });
    reader.readAsDataURL(file);
  };

  const onCropComplete = useCallback((_: any, croppedPixels: any) => {
    setCroppedAreaPixels(croppedPixels);
  }, []);

  const getCroppedImg = useCallback(async (imageSrcLocal: string, pixelCrop: any) => {
    const image = await new Promise<HTMLImageElement>((resolve, reject) => {
      const img = new Image();
      img.setAttribute('crossOrigin', 'anonymous');
      img.onload = () => resolve(img);
      img.onerror = (e) => reject(e);
      img.src = imageSrcLocal;
    });

    const canvas = document.createElement('canvas');
    canvas.width = pixelCrop.width;
    canvas.height = pixelCrop.height;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('No 2d context');

    ctx.drawImage(
      image,
      pixelCrop.x,
      pixelCrop.y,
      pixelCrop.width,
      pixelCrop.height,
      0,
      0,
      pixelCrop.width,
      pixelCrop.height
    );

    return canvas.toDataURL('image/jpeg');
  }, []);

  const applyCrop = useCallback(async () => {
    if (!imageSrc || !croppedAreaPixels) return;
    try {
      const cropped = await getCroppedImg(imageSrc, croppedAreaPixels);
      setCroppedImage(cropped);
      setShowCropper(false);
    } catch (err) {
      console.error('Crop error', err);
    }
  }, [imageSrc, croppedAreaPixels, getCroppedImg]);

  const clearImage = () => {
    setImageSrc(null);
    setCroppedImage(null);
    setShowCropper(false);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
  };

  const handleSaveAvatar = async () => {
    if (!croppedImage || isSavingAvatar) return;

    setIsSavingAvatar(true);
    try {
      await dispatch(userActions.updateUserAvatar(croppedImage));
      dispatch(notificationActions.addNotification('Đã cập nhật avatar thành công', 'SUCCESS'));
      clearImage();
      setShowAvatarModal(false);
    } catch (error: any) {
      console.error('Error saving avatar:', error);
      dispatch(notificationActions.addNotification('Lỗi khi cập nhật avatar', 'DANGER'));
    } finally {
      setIsSavingAvatar(false);
    }
  };

  const displayName = studentInfo?.name || currentUser?.name || currentUser?.userName || 'Học sinh';
  const giftPoints = studentInfo?.gift || currentUser?.total_points || 0;

  return (
    <>
      <HeaderContainer>
        <AvatarSection>
          <div style={{ position: 'relative' }}>
            <Avatar 
              src={currentUser?.avatar ? `${process.env.REACT_APP_API_URL}${currentUser.avatar}` : undefined}
              onClick={handleAvatarClick}
            >
              {!currentUser?.avatar && '👤'}
              <EditOverlay className="edit-overlay">
                📷
              </EditOverlay>
            </Avatar>
            <HiddenFileInput
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileChange}
            />
          </div>

          <StudentInfo>
            <StudentNameRow>
              <StudentName>{displayName}</StudentName>
            </StudentNameRow>
            <StudentUserNameRow>
              <StudentUserName>{currentUser?.userName}</StudentUserName>
              <PointsSection>
                <Points>{giftPoints}</Points>
                <Star>⭐</Star>
              </PointsSection>
            </StudentUserNameRow>
          </StudentInfo>
        </AvatarSection>

        <HeaderActions>
          <NotificationBell studentId={currentUser?.studentId} currentStudentId={currentUser?.studentId} />
          <LogoutButton onClick={onLogout}>Đăng xuất</LogoutButton>
          <LogoutButton style={{ background: '#3b82f6', marginTop: 0 }} onClick={() => setShowChangePassword(true)}>
            Đổi mật khẩu
          </LogoutButton>
        </HeaderActions>
      </HeaderContainer>

      {/* Review card — injected between profile and vocab stats */}
      {onReviewClick && (
        <div
          onClick={onReviewClick}
          style={{
            background: 'white',
            borderRadius: 12,
            padding: 20,
            boxShadow: '0 4px 6px rgba(0,0,0,0.07)',
            cursor: 'pointer',
            transition: 'all 0.2s',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 16,
          }}
          onMouseEnter={e => { (e.currentTarget as HTMLDivElement).style.boxShadow = '0 8px 20px rgba(102,126,234,0.2)'; (e.currentTarget as HTMLDivElement).style.transform = 'translateY(-2px)'; }}
          onMouseLeave={e => { (e.currentTarget as HTMLDivElement).style.boxShadow = '0 4px 6px rgba(0,0,0,0.07)'; (e.currentTarget as HTMLDivElement).style.transform = ''; }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <div style={{
              width: 48, height: 48, borderRadius: 12, flexShrink: 0,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 22,
            }}>📖</div>
            <div>
              <div style={{ fontSize: 20, fontWeight: 700, color: '#0f172a', marginBottom: 2 }}>Ôn tập bài cũ</div>
              <div style={{ fontSize: 13, color: '#64748b' }}>Xem lại từ vựng và mẫu câu từ các buổi học trước</div>
            </div>
          </div>
          <div style={{
            background: 'linear-gradient(135deg, #667eea, #764ba2)',
            color: '#fff', borderRadius: 20, padding: '8px 18px',
            fontWeight: 700, fontSize: 13, whiteSpace: 'nowrap',
            boxShadow: '0 3px 10px rgba(102,126,234,0.3)',
          }}>
            Ôn tập bài đã học →
          </div>
        </div>
      )}

      <VocabCard>
        <VocabHeader>
          <VocabTitle>📚 Thống kê từ vựng</VocabTitle>
          <VocabButton onClick={handleVocabClick}>
            📚 Học từ vựng
          </VocabButton>
        </VocabHeader>

        <StatsGrid>
          <StatItem bg="#f8fafc" color="#475569">
            <span className="label">Tổng bộ từ:</span>
            <span className="value">{vocabStats?.totalSets || 0}</span>
          </StatItem>
          <StatItem bg="#eff6ff" color="#3b82f6">
            <span className="label">Từ vựng:</span>
            <span className="value">{vocabStats?.totalCards || 0}</span>
          </StatItem>
          <StatItem bg="#f0fdf4" color="#22c55e">
            <span className="label">Đã thuộc:</span>
            <span className="value">{vocabStats?.masteredCards || 0}</span>
          </StatItem>
          <StatItem bg="#fefce8" color="#eab308">
            <span className="label">Đang học:</span>
            <span className="value">{vocabStats?.learningCards || 0}</span>
          </StatItem>
        </StatsGrid>

        {/* Pending Challenges Section */}
        <div style={{ marginTop: '16px' }}>
          <PendingChallengesSection studentId={currentUser?.studentId} />
        </div>
      </VocabCard>

      <Modal show={showAvatarModal} onHide={() => setShowAvatarModal(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Chỉnh sửa ảnh đại diện</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: '20px' }}>
          {showCropper && imageSrc ? (
            <div>
              <div style={{ position: 'relative', width: '100%', height: 400, background: '#333', borderRadius: '8px', overflow: 'hidden', marginBottom: '20px' }}>
                <Cropper
                  image={imageSrc}
                  crop={crop}
                  zoom={zoom}
                  aspect={1}
                  onCropChange={setCrop}
                  onCropComplete={onCropComplete}
                  onZoomChange={setZoom}
                />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                <span style={{ fontSize: '14px', fontWeight: 'bold' }}>Zoom</span>
                <input 
                  type="range" 
                  min={1} 
                  max={3} 
                  step={0.1} 
                  value={zoom}
                  onChange={(e) => setZoom(Number(e.target.value))}
                  style={{ flex: 1 }}
                />
                <span style={{ fontSize: '14px', minWidth: '40px' }}>{zoom.toFixed(1)}x</span>
              </div>
              <div style={{ display: 'flex', gap: 12 }}>
                <Button variant="primary" onClick={applyCrop} style={{ flex: 1 }}>
                  ✂️ Xác nhận cắt
                </Button>
                <Button variant="secondary" onClick={clearImage} style={{ flex: 1 }}>
                  🔄 Chọn lại
                </Button>
              </div>
            </div>
          ) : croppedImage ? (
            <div style={{ textAlign: 'center' }}>
              <h5 style={{ marginBottom: '16px' }}>Xem trước ảnh</h5>
              <img 
                src={croppedImage} 
                alt="Cropped" 
                style={{
                  width: '200px',
                  height: '200px',
                  borderRadius: '50%',
                  border: '3px solid #667eea',
                  objectFit: 'cover',
                  marginBottom: '16px'
                }}
              />
              <div style={{ display: 'flex', gap: 12 }}>
                <Button 
                  variant="success" 
                  onClick={handleSaveAvatar}
                  disabled={isSavingAvatar}
                  style={{ flex: 1 }}
                >
                  {isSavingAvatar ? 'Đang lưu...' : '💾 Lưu ảnh'}
                </Button>
                <Button variant="secondary" onClick={clearImage} style={{ flex: 1 }}>
                  🔄 Chọn lại
                </Button>
              </div>
            </div>
          ) : null}
        </Modal.Body>
      </Modal>

      <ChangePasswordModal
        show={showChangePassword}
        onClose={() => setShowChangePassword(false)}
        onSubmit={async (oldPassword, newPassword) => {
          // Trả lỗi về cho modal, chỉ đóng modal khi thành công
          try {
            await studentService.changePassword(oldPassword, newPassword);
            dispatch(notificationActions.addNotification('Đổi mật khẩu thành công', 'SUCCESS'));
            setShowChangePassword(false);
            return null;
          } catch (error: any) {
            // Trả lỗi về cho modal để hiển thị chữ đỏ
            return error?.toString() || 'Đổi mật khẩu thất bại';
          }
        }}
      />
    </>
  );
};

export default StudentProfileSection;
